import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import MockAdapter from 'axios-mock-adapter';
import posthog from 'posthog-js';
import {
  buildApiErrorEventProperties,
  buildPostHogApiException,
  extractApiErrorMessage,
  generateCurlCommand,
  reportApiError,
  shouldCaptureApiException
} from '@/lib/error-utils';
import { authorizationHeader, isPublicAuthRequest } from './session-access-core';
import { start, stop } from '@/lib/api-loading-store';
import { sessionAccess } from './session-access';
import { toast } from '@/hooks/use-toast';
import { env } from '@/platform/env';

interface ApiConfig<T = any> extends AxiosRequestConfig {
  mock?: boolean;
  mockData?: T[];
}

// PostHog logging utilities
const trackFailure = (error: AxiosError, requestId: string) => {
  // Only log to PostHog in production, not in local development
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('localhost'));

  // Skip logging in development, localhost, or if PostHog not properly initialized
  if (!posthog || !posthog.__loaded || isDevelopment || isLocalhost) return;

  if (error?.code === 'ERR_CANCELED') return;

  const eventProperties = buildApiErrorEventProperties({
    error,
    requestId,
    href: typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof window !== 'undefined' ? navigator.userAgent : null
  });

  // Event Explorer: filter by api_request_failed and copy curl_command.
  posthog.capture('api_request_failed', {
    ...eventProperties
  });

  // Error Tracking: same request_id lets us jump back to the detailed event.
  if (typeof posthog.captureException === 'function' && shouldCaptureApiException(error)) {
    posthog.captureException(buildPostHogApiException(error, eventProperties), eventProperties);
  }
};

const newRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const shouldToastError = (method?: string, status?: number): boolean => {
  if (status === 401) return false;
  return ['post', 'put', 'patch', 'delete'].includes(method?.toLowerCase() || '');
};

// Create base axios instance
const http = axios.create({
  baseURL: env.apiUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'vi, en;q=0.1'
  }
});

class ApiClient {
  private client = http;
  private mocker: MockAdapter | null = null;

  private getMock() {
    if (!this.mocker) {
      this.mocker = new MockAdapter(this.client, {
        delayResponse: 300,
        onNoMatch: 'passthrough'
      });
    }
    return this.mocker;
  }

  private mockList<T>(mockData?: T[]): any {
    if (mockData && mockData.length > 0) {
      const response = {
        count: mockData.length,
        next: null,
        previous: null,
        results: mockData
      };
      return response;
    }

    const emptyResponse = {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
    return emptyResponse;
  }

  async get<T = any>(url: string, config?: ApiConfig): Promise<AxiosResponse<T>> {
    if (config?.mock === true) {
      const mock = this.getMock();
      const mockResponse = this.mockList(config.mockData);

      mock.onGet(url).reply(200, mockResponse);

      try {
        const { mock: _, mockData: __, ...cleanConfig } = config;
        const response = await this.client.get<T>(url, cleanConfig);
        mock.reset();
        return response;
      } catch (error) {
        mock.reset();
        throw error;
      }
    }
    return this.client.get<T>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  get interceptors() {
    return this.client.interceptors;
  }
  resetMock() {
    if (this.mocker) {
      this.mocker.reset();
    }
  }
}

// Create enhanced API instance
const api = new ApiClient();

// Add request interceptor to include auth token and request ID
api.interceptors.request.use(
  config => {
    // Add auth token
    const token = localStorage.getItem('auth_token');
    if (token && !authorizationHeader(config.headers) && !isPublicAuthRequest(config.url)) {
      config.headers['Authorization'] = `Token ${token}`;
    }

    // Ensure Accept-Language header is present on every request
    if (!config.headers['Accept-Language']) {
      config.headers['Accept-Language'] = 'vi, en;q=0.1';
    }

    // Add unique request ID for tracking
    const requestId = newRequestId();
    config.metadata = { requestId, startTime: Date.now(), activityTracked: true };
    start(config.method);

    return config;
  },
  error => {
    if (error?.config?.metadata?.activityTracked) {
      stop(error.config.method);
    }
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors and log failures
api.interceptors.response.use(
  response => {
    if (response?.config?.metadata?.activityTracked) {
      response.config.metadata.activityTracked = false;
      stop(response.config.method);
    }
    return response;
  },
  async error => {
    if (error?.config?.metadata?.activityTracked) {
      error.config.metadata.activityTracked = false;
      stop(error.config.method);
    }
    // Get request ID from metadata
    const requestId = error.config?.metadata?.requestId || newRequestId();

    // Log failed request to PostHog (PRODUCTION ONLY)
    trackFailure(error, requestId);

    await sessionAccess.handleError(error);

    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  res => {
    const data = res?.data;
    const method = res?.config?.method?.toLowerCase();
    const status = res?.status;
    const shouldToastSuccess = method === 'put' || method === 'delete' || status === 201;

    if (data?.success === false) {
      const description = extractApiErrorMessage({ status, data });
      if (shouldToastError(method, status)) {
        toast.error(description);
      }
      return Promise.reject(Object.assign(new Error(description), { response: res }));
    }

    if (shouldToastSuccess) {
      if (typeof data?.message === 'string' && data.message.trim()) {
        toast.success(data.message, {
          duration: 3000
        });
      } else {
        toast.success('Thành công', {
          duration: 3000
        });
      }
    } else if (typeof data?.message === 'string' && data.message.trim()) {
      toast.success(data.message, {
        duration: 3000
      });
    }

    return res;
  },
  error => {
    const status = error?.response?.status;
    const data = error?.response?.data;

    // Use the new utility function to extract error message
    const description = extractApiErrorMessage({ status, data });

    const curl = generateCurlCommand(error.config);
    const responseStr = JSON.stringify(data || {}, null, 2);
    const logStr = `${error.message}\n${error.stack || ''}\nError Code: ${error.code || 'N/A'}`;
    const url = error.config?.url;

    reportApiError({
      message: description,
      curl,
      response: responseStr,
      log: logStr,
      url,
      status
    });

    if (shouldToastError(error?.config?.method, status)) {
      toast.error(description);
    }

    return Promise.reject(error);
  }
);

export default api;
